import { Component, inject, OnDestroy, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NgbDropdownModule } from '@ng-bootstrap/ng-bootstrap';
import { Subject } from 'rxjs';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { AccountSelectorComponent } from '../../../../../_shared/components/account-selector/account-selector.component';
import { ErrorStateComponent } from '../../../../../_shared/components/states/error-state/error-state.component';
import { Account } from '../../../../../_shared/models';
import { MaintenanceConfigurationService } from '../../services/maintenance-configuration.service';
import { createDefaultConfiguration } from '../../helpers/maintenance-configuration.helper';
import { MaintenanceConfigurationResponse } from '../../models/maintenance-configuration-response.model';
import { UpsertConfigurationRequest } from '../../models/maintenance-configuration-request.model';
import { ToastrService } from 'ngx-toastr';
import { generateNumbers } from '../../../../../_shared/helpers/generate-numbers/generate-numbers.helper';

@Component({
  selector: 'app-maintenance-configuration',
  standalone: true,
  imports: [CommonModule, FormsModule, NgbDropdownModule, TranslocoDirective, AccountSelectorComponent, ErrorStateComponent],
  templateUrl: './maintenance-configuration.component.html',
  styleUrl: './maintenance-configuration.component.scss'
})
export class MaintenanceConfigurationComponent implements OnDestroy {
  @ViewChild(AccountSelectorComponent) accountSelector!: AccountSelectorComponent;

  selectedAccountId: number | null = null;
  configuration: MaintenanceConfigurationResponse | null = null;
  hasError: boolean = false;
  isLoading: boolean = false;

  private cancelSubject = new Subject<void>();
  private saveTimeout: ReturnType<typeof setTimeout> | null = null;

  private configService = inject(MaintenanceConfigurationService);
  private toastr = inject(ToastrService);
  private transloco = inject(TranslocoService);

  leadTimeOptions = [
    { label: this.transloco.translate('assetSettings.maintenance.leadTime.days7'), value: 7 },
    { label: this.transloco.translate('assetSettings.maintenance.leadTime.days14'), value: 14 },
    { label: this.transloco.translate('assetSettings.maintenance.leadTime.days30'), value: 30 }
  ];

  jobsPerDayOptions = generateNumbers({ length: 20 });
  hoursPerDayOptions = generateNumbers({ length: 24 });

  ngOnDestroy(): void {
    this.cancelSubject.next();
    this.cancelSubject.complete();
    if (this.saveTimeout) {
      clearTimeout(this.saveTimeout);
    }
  }

  get leadTimeLabel(): string {
    return this.leadTimeOptions.find(o => o.value === this.configuration?.notificationLeadTimeDays)?.label
      ?? this.transloco.translate('assetSettings.maintenance.leadTime.days30');
  }

  async onAccountChanged(account: Account): Promise<void> {
    if (!account?.id) {
      this.selectedAccountId = null;
      this.configuration = null;
      this.hasError = false;
      return;
    }

    this.selectedAccountId = account.id;
    await this.loadConfiguration();
  }

  async loadConfiguration(): Promise<void> {
    if (!this.selectedAccountId) return;

    this.hasError = false;
    this.configuration = null;
    this.isLoading = true;

    try {
      this.cancelSubject.next();
      const response = await this.configService.getConfiguration(this.selectedAccountId, this.cancelSubject);

      if (response.data) {
        this.configuration = response.data;
      } else {
        // No configuration exists — create defaults with everything disabled
        this.configuration = createDefaultConfiguration(this.selectedAccountId);
        const request = this.getConfigurationRequest();

        await this.configService.upsertConfiguration(request);
      }

      this.isLoading = false;
    } catch (error: any) {
      this.selectedAccountId = null;
      this.hasError = true;
      this.isLoading = false;
      this.accountSelector?.clearSelection();
    }
  }

  onLeadTimeChange(value: number): void {
    if (!this.configuration) return;
    this.configuration.notificationLeadTimeDays = value;
    this.onSettingChanged();
  }

  onMaxJobsChange(value: number): void {
    if (!this.configuration) return;
    this.configuration.maxJobsPerDay = value;
    this.onSettingChanged();
  }

  onMaxHoursChange(value: number): void {
    if (!this.configuration) return;
    this.configuration.maxHoursPerDay = value;
    this.onSettingChanged();
  }

  onToggleChange(): void {
    this.onSettingChanged();
  }

  private getConfigurationRequest(): UpsertConfigurationRequest {
    return {
      accountId: this.configuration!.accountId,
      notificationLeadTimeDays: this.configuration!.notificationLeadTimeDays,
      maxJobsPerDay: this.configuration!.maxJobsPerDay,
      maxHoursPerDay: this.configuration!.maxHoursPerDay,
      autoApproveOemRequest: this.configuration!.autoApproveOemRequest,
      autoApproveDriverRequest: this.configuration!.autoApproveDriverRequest,
      autoApproveWorkshopRequest: this.configuration!.autoApproveWorkshopRequest,
      autoApproveComplianceRequest: this.configuration!.autoApproveComplianceRequest,
      autoApproveCanBusRequest: this.configuration!.autoApproveCanBusRequest,
      autoAssignTechnician: this.configuration!.autoAssignTechnician,
      autoApproveWorkOrderRequest: this.configuration!.autoApproveWorkOrderRequest,
    }
  }

  private onSettingChanged(): void {
    if (!this.configuration) return;

    // Debounce to batch rapid changes
    if (this.saveTimeout) {
      clearTimeout(this.saveTimeout);
    }

    this.saveTimeout = setTimeout(async () => {
      try {
        const request = this.getConfigurationRequest();
        await this.configService.upsertConfiguration(request);
        this.hasError = false;
        this.toastr.success(this.transloco.translate('assetSettings.common.settingsSaved'));
      } catch (error: any) {
        this.toastr.error(this.transloco.translate('assetSettings.common.settingsSaveFailed'));
        this.loadConfiguration();
      }
    }, 1000);
  }
}